import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { TextField, Grid, Paper, Box, LinearProgress } from "@mui/material";
import Button from "@/components/button";
import { Formik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@apollo/client";
import { PASSWORD_LOGIN } from "@/services/authService";

import AuthActions from "@/store/auth/actions";

import images from "@/assets/images";
import banner from "@/assets/images/login/banner.png";
import useStyles from "./styles";
import { MERCHANT_ID } from "@/constants/env";

const LoginPage = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("login_screen.enter_valid_email"))
      .required(t("login_screen.username_required")),
    password: Yup.string().required(t("login_screen.password_required")),
  });

  const [loginError, setLoginError] = useState("");
  const [forgotClicked, setForgotClicked] = useState(false);

  const [passwordLogin, { loading }] = useMutation(PASSWORD_LOGIN, {
    onCompleted: (data) => {
      const merchant = data.password_login.user?.merchants?.find(
        (item) => item.id === MERCHANT_ID
      );
      if (!!merchant) {
        dispatch(AuthActions.onLoginSuccess(data.password_login));
      } else {
        setLoginError(t("login_screen.unauthorised_access"));
      }
    },
    onError: (error) => {
      const message = error?.message || error?.errors[0]?.message;
      setLoginError(message);
    },
  });
  const onLogin = (values) => {
    setLoginError("");
    passwordLogin({
      variables: {
        email: values.email,
        password: values.password,
      },
    });
  };

  const onClickForgot = () => {
    if (loading) return;
    if (forgotClicked) {
    } else {
      setForgotClicked(true);
    }
  };

  return (
    <Grid container component={"main"} sx={{ height: "100vh" }}>
      <Grid
        item
        sm={4}
        md={7}
        sx={{
          display: { xs: "none", sm: "block" },
          backgroundImage: `url(${banner})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          width={"80%"}
          height={"100%"}
          margin={"auto"}
        >
          <Box sx={{ marginTop: "15vh" }}>
            <img src={images.logo} width={150} height={60} />
          </Box>

          <Box marginTop={6} marginBottom={3}>
            <span className={classes.signInText}>
              {t("login_screen.sign_in")}
            </span>
          </Box>
          <Formik
            initialValues={{ email: "", password: "" }}
            validationSchema={validationSchema}
            onSubmit={onLogin}
          >
            {({
              handleBlur,
              handleSubmit,
              values,
              errors,
              touched,
              setFieldValue,
            }) => (
              <Box component="form" noValidate onSubmit={handleSubmit}>
                <TextField
                  margin="normal"
                  fullWidth
                  label={t("login_screen.username")}
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={values.email}
                  onChange={(e) => setFieldValue("email", e.target.value)}
                  onBlur={handleBlur}
                  error={touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                />
                <TextField
                  margin="normal"
                  fullWidth
                  name="password"
                  label={t("login_screen.password")}
                  type="password"
                  value={values.password}
                  onChange={(e) => setFieldValue("password", e.target.value)}
                  onBlur={handleBlur}
                  error={
                    !!((touched.password && !!errors.password) || !!loginError)
                  }
                  helperText={
                    loginError || (touched.password && errors.password)
                  }
                />
                <Button
                  disabled={loading}
                  fullWidth={true}
                  color="primary"
                  type="submit"
                  variant="contained"
                  sx={{ mt: 4, mb: 2 }}
                  className={classes.signinButton}
                >
                  {t("login_screen.sign_in")}
                </Button>
                {loading && (
                  <Box sx={{ width: "100%" }} marginTop={6}>
                    <LinearProgress />
                  </Box>
                )}
              </Box>
            )}
          </Formik>
          <Box marginTop={6} marginBottom={3}>
            <span className={classes.forgotText} onClick={onClickForgot}>
              {forgotClicked
                ? t("login_screen.reset_password")
                : t("login_screen.forgot_password")}
            </span>
          </Box>
          <Box marginTop={10} marginBottom={3} textAlign={"center"}>
            <span className={classes.contactText}>
              {t("login_screen.contact_your_admin")}
            </span>
          </Box>
          <Box
            flex={"auto"}
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"flex-end"}
            marginBottom={6}
            textAlign={"center"}
          >
            <span className={classes.contactText}>
              {t("login_screen.copyright", { year: new Date().getFullYear() })}
            </span>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default LoginPage;
